import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articles } from "@/mocks/articles";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;

  if (!article) {
    return (
      <div className="min-h-screen pt-24 pb-14 flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <article className="min-h-screen pt-24 pb-14">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.imageUrl,
            datePublished: article.time,
            author: {
              "@type": "Organization",
              name: article.source,
            },
            publisher: {
              "@type": "Organization",
              name: "51暗网吃瓜",
              url: "http://hnwmjg.cn/",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `http://hnwmjg.cn/article/${article.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "http://hnwmjg.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.category,
                item: 'http://hnwmjg.cn/',
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
            {t("breadcrumb.home")}
          </Link>
          <span>/</span>
          <Link
            to="/"
            className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
          >
            {article.category}
          </Link>
          <span>/</span>
          <span className="text-foreground-700 line-clamp-1">{article.title}</span>
        </nav>

        <header className="mb-8">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 mb-4">
            {article.category}
          </span>
          <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
            <span>{t("article.time")} {article.time}</span>
            <span>{t("article.source")}：{article.source}</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
        </header>

        <div className="mb-8 rounded-lg overflow-hidden aspect-[21/9]">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="prose max-w-none mb-10">
          {article.content.split("\n").map((paragraph, idx) => (
            <p key={idx} className="text-base text-foreground-700 leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 cursor-pointer whitespace-nowrap hover:bg-secondary-200 transition-colors"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-background-200">
          <span className="text-sm text-foreground-400">
            {t("article.prev")}
          </span>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-share-forward-line text-foreground-500 text-lg"></i>
            </div>
          </div>
          <span className="text-sm text-foreground-400">
            {t("article.next")}
          </span>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              {t("article.related")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to="/"
                  className="group block bg-background-50 rounded-lg overflow-hidden cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-400 mt-1 block">{ra.time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}